import { CheckCircle2, AlertCircle } from 'lucide-react';

/**
 * DataCompleteness card
 *
 * Renders the per-source availability checklist that appears between the
 * score and the narrative. Each item shows a green check or a gray warning
 * with a short detail string. Designed to be honest about coverage gaps
 * without overwhelming the report visually.
 *
 * Expects data prop with shape:
 *   {
 *     percent_complete: 78,
 *     summary: "...",
 *     items: [{ key, label, available, detail }]
 *   }
 */
export default function DataCompleteness({ data }) {
  if (!data || !Array.isArray(data.items) || data.items.length === 0) return null;

  const isComplete = data.percent_complete >= 100;
  const headerColor = isComplete
    ? 'text-green-700'
    : data.percent_complete >= 70
      ? 'text-amber-600'
      : 'text-orange-600';

  return (
    <div className="card mb-6 animate-fade-in-up" style={{ animationDelay: '0.2s', animationFillMode: 'both' }}>
      <div className="flex items-center justify-between mb-3">
        <h2 className="section-title mb-0">Data coverage for this location</h2>
        <div className={`text-sm font-bold ${headerColor}`}>
          {data.percent_complete}% complete
        </div>
      </div>

      <div className="text-xs text-gray-500 mb-4">
        {data.summary}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {data.items.map((item, i) => (
          <CompletenessItem key={i} item={item} />
        ))}
      </div>
    </div>
  );
}

function CompletenessItem({ item }) {
  const isAvailable = item.available;

  return (
    <div className={`flex items-start gap-2.5 py-1.5 px-2 rounded ${
      isAvailable ? '' : 'bg-amber-50'
    }`}>
      <div className="flex-shrink-0 mt-0.5">
        {isAvailable
          ? <CheckCircle2 size={16} className="text-green-600" />
          : <AlertCircle size={16} className="text-amber-500" />
        }
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-medium ${isAvailable ? 'text-gray-700' : 'text-amber-800'}`}>
          {item.label}
        </div>
        <div className={`text-xs ${isAvailable ? 'text-gray-500' : 'text-amber-700'}`}>
          {item.detail}
        </div>
      </div>
    </div>
  );
}
